import { auth } from '@/firebase/config'

const API_URL = import.meta.env.VITE_API_URL

interface ApiConfig {
	timeout?: number
	headers?: Record<string, string>
	responseType?: string
	redirect?: RequestRedirect
}

class ApiService {
	private async getAuthToken(): Promise<string> {
		await auth.authStateReady()
		const user = auth.currentUser
		if (!user) {
			throw new Error('No hay sesión activa. Vuelve a iniciar sesión.')
		}
		const token = await user.getIdToken()
		if (!token) {
			throw new Error('No hay sesión activa. Vuelve a iniciar sesión.')
		}
		return token
	}

	private async request<T>(method: string, path: string, params?: unknown, customConfig?: ApiConfig): Promise<T> {
		// Activar el loader antes de la petición

		const config: ApiConfig = {
			timeout: 120000, // 2 minutes default timeout
			headers: {
				'Content-Type': 'application/json; charset=UTF-8',
				'Accept': 'application/json, text/plain, */*'
			}
		}

		// Apply custom config if provided
		if (customConfig) {
			Object.assign(config, customConfig)
		}

		// Add auth token to headers
		try {
			const token = await this.getAuthToken()
			config.headers = {
				...config.headers,
				'Authorization': `${token}`
			}
		} catch (error) {
			console.error('Error getting auth token:', error)
			throw error
		}

		// Build full URL - ensure proper URL construction
		const baseUrl = API_URL?.endsWith('/') ? API_URL?.slice(0, -1) : API_URL
		const cleanPath = path.startsWith('/') ? path : `/${path}`
		const url = path.startsWith('http') ? path : `${baseUrl}${cleanPath}`

		// Prepare request options
		const requestOptions: RequestInit = {
			method,
			headers: config.headers,
			mode: 'cors', // Explicitly set CORS mode
			redirect: config.redirect
		}

		// Add body for POST/PUT requests
		if (params && (method === 'POST' || method === 'PUT')) {
			if (params instanceof FormData) {
				// For FormData, let the browser set the Content-Type
				delete config.headers['Content-Type']
				requestOptions.body = params
			} else {
				requestOptions.body = JSON.stringify(params)
			}
		}

		// Make the request
		try {
			const response = await fetch(url, requestOptions)

			if (!response.ok) {
				// Intentar obtener el mensaje de error de forma segura
				let errorMessage = `HTTP error! status: ${response.status}`

				try {
					const contentType = response.headers.get('content-type')
					if (contentType && contentType.includes('application/json')) {
						const errorData = await response.json()
						errorMessage = errorData.message || errorData.error || errorMessage
					} else {
						// Si no es JSON, leer como texto
						const errorText = await response.text()
						errorMessage = errorText?.trim() ? errorText : errorMessage
					}
				} catch {
					// Si falla el parseo, intentar leer como texto
					try {
						const errorText = await response.text()
						errorMessage = errorText?.trim() ? errorText : errorMessage
					} catch (textError) {
						// Si también falla, usar el mensaje por defecto
						console.error('Error al leer respuesta de error:', textError)
					}
				}

				throw new Error(errorMessage)
			}
			// Handle response based on responseType
			let result: T
			if (config.responseType === 'arraybuffer') {
				const arrayBuffer = await response.arrayBuffer()
				result = arrayBuffer as unknown as T
			} else if (config.responseType === 'blob') {
				const blob = await response.blob()
				result = blob as unknown as T
			} else if (response.headers.get('content-type')?.includes('application/json')) {
				try {
					const data = await response.json()
					result = data as T
				} catch (error) {
					console.warn('Error al procesar la respuesta del servidor:', error)
					throw new TypeError('Respuesta JSON inválida del servidor')
				}
			} else {
				result = response.text() as unknown as T
			}

			// Desactivar el loader después de recibir la respuesta
			return result
		} catch (error) {
			console.error(`API request error (${method} ${path}):`, error)
			if (error instanceof Error) {
				throw error
			}
			if (typeof error === 'string') {
				throw new Error(error)
			}
			throw new Error('Error al enviar la solicitud')
		}
	}

	async post<T = unknown>(path: string, params?: unknown, config?: ApiConfig): Promise<T> {
		return this.request<T>('POST', path, params, config)
	}

	async put<T = unknown>(path: string, params?: unknown, config?: ApiConfig): Promise<T> {
		return this.request<T>('PUT', path, params, config)
	}

	async get<T = unknown>(path: string, config?: ApiConfig): Promise<T> {
		return this.request<T>('GET', path, undefined, config)
	}

	async delete<T = unknown>(path: string, config?: ApiConfig): Promise<T> {
		return this.request<T>('DELETE', path, undefined, config)
	}

	async callAuth<T = unknown>(method: string, path: string, params?: unknown, customConfig?: ApiConfig): Promise<T> {
		await auth.authStateReady()
		const user = auth.currentUser
		if (!user) {
			throw new Error('No hay sesión activa. Vuelve a iniciar sesión.')
		}
		const idToken = await user.getIdToken()
		if (!idToken) {
			throw new Error('No hay sesión activa. Vuelve a iniciar sesión.')
		}

		const config: ApiConfig = {
			timeout: 120000,
			headers: {
				Authorization: idToken
			}
		}

		if (customConfig) {
			Object.assign(config, customConfig)
		}

		return this.request<T>(method, path, params, config)
	}
}

export const apiService = new ApiService()
